import React, { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";

interface AvatarProvider {
  slug: string;
  displayName: string;
}

interface BotOverride {
  id: number;
  name: string;
  enableAvatar: boolean;
}

interface AvatarConfig {
  siteId: number;
  providerSlug: string;
  avatarId: string;
  voiceId: string;
  enabled: boolean;
  showCallMeOption: boolean;
  callbackConfirmWindowMinutes: number;
}

interface UsageMetric {
  name: string;
  displayName: string;
  current: number;
  max: number;
  percent: number;
  isUnlimited: boolean;
}

interface SiteUsageSummary {
  siteId: number;
  metrics: UsageMetric[];
}

const PROVIDER_DESCRIPTIONS: Record<string, string> = {
  simli: "Real-time WebRTC streaming. Lowest latency. Best for live chat.",
  "d-id": "High-fidelity video synthesis. Photorealistic. Slight processing delay.",
  heygen: "Cinema-quality avatars. Highest visual quality. Enterprise tier.",
  none: "Disable video avatars.",
};

const AVATAR_ID_PLACEHOLDERS: Record<string, string> = {
  simli: "e.g. tmp9i8bbq7",
  "d-id": "e.g. rian-lZC6MmWfC1",
  heygen: "e.g. Angela-inTshirt-20220820",
};

const DEFAULT_CONFIG: AvatarConfig = {
  siteId: 0,
  providerSlug: "none",
  avatarId: "",
  voiceId: "",
  enabled: false,
  showCallMeOption: false,
  callbackConfirmWindowMinutes: 10,
};

export default function AvatarSettings() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const toast = useToastAlert();

  const [config, setConfig] = useState<AvatarConfig>(DEFAULT_CONFIG);
  const [providers, setProviders] = useState<AvatarProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usageMetric, setUsageMetric] = useState<UsageMetric | null>(null);
  const [botOverrides, setBotOverrides] = useState<BotOverride[]>([]);
  const [togglingBotId, setTogglingBotId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("avatar/config");
      if (resp.ok) {
        const data = await resp.json();
        setConfig(data.config ?? DEFAULT_CONFIG);
        setProviders(Array.isArray(data.providers) ? data.providers : []);
      }
    } catch {
      toast.displayToast(Severity.Error, "Failed to load avatar configuration.");
    } finally {
      setLoading(false);
    }
  }, [api.messaging, toast]);

  const loadUsage = useCallback(async () => {
    try {
      const resp = await api.messaging.get("admin/billing/full-usage");
      if (resp.ok) {
        const data: SiteUsageSummary = await resp.json();
        const metric = (Array.isArray(data?.metrics) ? data.metrics : []).find(
          (m) => m.name === "avatarTtsChars"
        );
        setUsageMetric(metric ?? null);
      }
    } catch {
      // non-critical — usage panel is informational only
    }
  }, [api.messaging]);

  const loadBotOverrides = useCallback(async () => {
    try {
      const resp = await api.messaging.get("avatar/bot-overrides");
      if (resp.ok) {
        const data = await resp.json();
        setBotOverrides(Array.isArray(data) ? data : []);
      }
    } catch {
      // non-critical
    }
  }, [api.messaging]);

  useEffect(() => {
    load();
    loadUsage();
    loadBotOverrides();
  }, [load, loadUsage, loadBotOverrides]);

  const toggleBotAvatar = async (botId: number, currentValue: boolean) => {
    setTogglingBotId(botId);
    try {
      const resp = await api.messaging.post(`avatar/bot-overrides/${botId}/toggle`, { enabled: !currentValue });
      if (resp.ok) {
        setBotOverrides((prev) =>
          prev.map((b) => b.id === botId ? { ...b, enableAvatar: !currentValue } : b)
        );
      }
    } catch {
      // ignore
    } finally {
      setTogglingBotId(null);
    }
  };

  const updateConfig = (patch: Partial<AvatarConfig>) =>
    setConfig((c) => ({ ...c, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const resp = await api.messaging.post("avatar/config", config);
      if (resp.ok) {
        toast.displayToast(Severity.Success, "Avatar settings saved.");
      } else {
        toast.displayToast(Severity.Error, "Failed to save avatar settings.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Network error saving avatar settings.");
    } finally {
      setSaving(false);
    }
  };

  // Subscription gate
  if (!subscription?.enableAvatarMode) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="text-5xl mb-4">🎭</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">AI Video Avatars</h2>
          <p className="text-gray-600 mb-6">
            Give your bot a face. Choose a photorealistic AI avatar powered by Simli, D-ID, or
            HeyGen — with real-time lip-sync driven by your ElevenLabs voice.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-8 text-left">
            {([
              ["Real-Time Lip-Sync", "WebRTC streaming with sub-200ms latency via Simli"],
              ["Photorealistic Faces", "D-ID and HeyGen cinema-quality video avatars"],
              ["ElevenLabs Voice", "Your IVR voice drives the avatar's speech"],
              ["Per-Site Metering", "Track avatar TTS character usage per month"],
            ] as [string, string][]).map(([title, desc]) => (
              <div key={title} className="bg-gray-50 rounded-lg p-4">
                <div className="font-semibold text-gray-800">{title}</div>
                <div className="text-sm text-gray-600 mt-1">{desc}</div>
              </div>
            ))}
          </div>
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
            AI Video Avatars require an Enterprise plan. Contact support to upgrade.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading avatar settings...</div>;
  }

  const showPersonaSection = config.providerSlug !== "none";

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">

      {/* Provider Selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 text-lg mb-1">Video Avatar Provider</h3>
        <p className="text-sm text-gray-500 mb-5">
          Choose which AI video avatar service powers your bot&apos;s face. Velaro manages the
          API keys — you just pick the provider and persona.
        </p>
        <div className="space-y-3">
          {[
            ...providers,
            { slug: "none", displayName: "None" },
          ].map((p) => {
            const isSelected = config.providerSlug === p.slug;
            return (
              <label
                key={p.slug}
                className={`flex items-start gap-4 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                  isSelected
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name="providerSlug"
                  value={p.slug}
                  checked={isSelected}
                  onChange={() => updateConfig({ providerSlug: p.slug })}
                  className="mt-1 accent-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">{p.displayName}</div>
                  {PROVIDER_DESCRIPTIONS[p.slug] && (
                    <div className="text-sm text-gray-500 mt-0.5">
                      {PROVIDER_DESCRIPTIONS[p.slug]}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Avatar Persona */}
      {showPersonaSection && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 text-lg mb-5">Avatar Persona</h3>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avatar / Persona ID
              </label>
              <input
                type="text"
                value={config.avatarId}
                onChange={(e) => updateConfig({ avatarId: e.target.value })}
                placeholder={AVATAR_ID_PLACEHOLDERS[config.providerSlug] ?? "Avatar ID"}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use the provider&apos;s default avatar.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ElevenLabs Voice Override
              </label>
              <input
                type="text"
                value={config.voiceId}
                onChange={(e) => updateConfig({ voiceId: e.target.value })}
                placeholder="ElevenLabs voice ID (optional)"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use your IVR voice settings.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Behavior */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 text-lg mb-5">Behavior</h3>
        <div className="space-y-5">
          {/* Enabled toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800 text-sm">Show avatar in bot conversations</div>
            </div>
            <button
              type="button"
              aria-label={config.enabled ? "Disable avatar mode" : "Enable avatar mode"}
              onClick={() => updateConfig({ enabled: !config.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Show Call Me toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800 text-sm">
                Show &quot;Call me instead&quot; option
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Requires Click-to-Call to be enabled.
              </div>
            </div>
            <button
              type="button"
              aria-label={
                config.showCallMeOption
                  ? "Disable call me instead option"
                  : "Enable call me instead option"
              }
              onClick={() => updateConfig({ showCallMeOption: !config.showCallMeOption })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.showCallMeOption ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.showCallMeOption ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Callback window */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Callback offer expires after (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={60}
              value={config.callbackConfirmWindowMinutes}
              onChange={(e) =>
                updateConfig({
                  callbackConfirmWindowMinutes: Math.max(
                    1,
                    Math.min(60, parseInt(e.target.value, 10) || 10)
                  ),
                })
              }
              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Usage */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 text-lg mb-3">Usage</h3>
        {usageMetric ? (
          <div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-600">Avatar voice characters used this month</span>
              <span className="font-medium text-gray-900">
                {usageMetric.current.toLocaleString()}
                {!usageMetric.isUnlimited && (
                  <span className="text-gray-500">
                    {" / "}
                    {usageMetric.max.toLocaleString()}
                  </span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  usageMetric.isUnlimited
                    ? "bg-blue-400"
                    : usageMetric.percent >= 90
                    ? "bg-red-500"
                    : usageMetric.percent >= 70
                    ? "bg-orange-400"
                    : "bg-emerald-500"
                }`}
                style={{
                  width: usageMetric.isUnlimited
                    ? "100%"
                    : `${Math.min(100, usageMetric.percent)}%`,
                }}
              />
            </div>
            {!usageMetric.isUnlimited && (
              <p className="text-xs text-gray-500 mt-1">
                {usageMetric.percent.toFixed(1)}% of monthly limit used
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Avatar TTS usage data is not available yet. It will appear here once avatar
            conversations have occurred.
          </p>
        )}
      </div>

      {/* Per-Bot Overrides */}
      {botOverrides.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="font-semibold text-gray-900 text-lg mb-1">Per-Bot Avatar Control</h3>
          <p className="text-sm text-gray-500 mb-5">
            The settings above apply site-wide by default. Use these toggles to enable or disable
            the avatar for individual bots — useful when some bots are text-only (e.g. email or
            SMS bots) while others should show a video avatar.
          </p>
          <div className="divide-y divide-gray-100">
            {botOverrides.map((bot) => (
              <div key={bot.id} className="flex items-center justify-between py-3">
                <span className="text-sm font-medium text-gray-800">{bot.name || `Bot #${bot.id}`}</span>
                <button
                  type="button"
                  aria-label={bot.enableAvatar ? `Disable avatar for ${bot.name}` : `Enable avatar for ${bot.name}`}
                  onClick={() => toggleBotAvatar(bot.id, bot.enableAvatar)}
                  disabled={togglingBotId === bot.id}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    bot.enableAvatar ? "bg-blue-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      bot.enableAvatar ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Changes save immediately. Bots with avatar disabled will never start a video session,
            even if the site-wide avatar is enabled above.
          </p>
        </div>
      )}

      {/* Save */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
